// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Materialeflow (pkt. 10) as data and geometry (prototype "Variant C ·
 * Materialeflow"): the tonnes of every non-rejected type with tons, from the
 * type (left) to its treatment (right) — or to Blokeret while it blocks the
 * report (`bygning/blocking.ts`, the same list as "Det, der blokerer").
 * Bevaring is shown but stays in the building, so it is not counted as klar.
 *
 * `flowLayout` is the prototype's: one scale for both columns, fitted so the
 * taller one is about `fit` high while every node keeps a minimum slot (the
 * label needs room), nodes centred in their slots, and each ribbon a cubic
 * from its type to its target, stacked in row order on both ends. Colours are
 * token names; the component turns them into `var(--…)`.
 *
 * Pure: no React, no DOM.
 */

import { TREATMENTS, type Sample, type SurveyFractions, type SurveyType, type Treatment } from '../api/types';
import { formatTonnes, TREATMENT_LABEL } from '../kortlaegning/vocab';
import { blockingItems, type BlockReason } from './blocking';
import { reportedTypes } from './checklist';

export type FlowTarget = Treatment | 'blocked';

/** The right column's order: the waste hierarchy, best first, then Blokeret. */
export const FLOW_TARGETS: readonly FlowTarget[] = [...TREATMENTS, 'blocked'];

export interface FlowRow {
  typeId: number;
  name: string;
  tonnes: number;
  to: FlowTarget;
  /** Why it blocks; empty unless `to` is `blocked`. */
  reasons: BlockReason[];
}

export interface FlowData {
  /** Grouped by target (in `FLOW_TARGETS` order), heaviest first: fewer crossings. */
  rows: FlowRow[];
  /** Types without tons: not drawn, but named under the flow. */
  omitted: { typeId: number; name: string }[];
  totals: Partial<Record<FlowTarget, number>>;
  /** The targets that receive any tonnes, in order. */
  targets: FlowTarget[];
  /** Tonnes that go to a treatment now (bevaring and the blocked left out). */
  ready: number;
  blocked: number;
}

export function flowData(types: readonly SurveyType[], samples: readonly Sample[], fractions: SurveyFractions | null): FlowData {
  const blocking = new Map(blockingItems(types, samples, fractions).map((i) => [i.typeId, i.reasons]));
  const rows: FlowRow[] = [];
  const omitted: FlowData['omitted'] = [];
  for (const t of reportedTypes(types)) {
    if (t.mass_t === null || !(t.mass_t > 0)) {
      omitted.push({ typeId: t.id, name: t.name });
      continue;
    }
    const reasons = blocking.get(t.id) ?? [];
    rows.push({ typeId: t.id, name: t.name, tonnes: t.mass_t, to: reasons.length > 0 ? 'blocked' : t.treatment, reasons });
  }
  rows.sort((a, b) => FLOW_TARGETS.indexOf(a.to) - FLOW_TARGETS.indexOf(b.to) || b.tonnes - a.tonnes);
  const totals: FlowData['totals'] = {};
  for (const r of rows) totals[r.to] = (totals[r.to] ?? 0) + r.tonnes;
  const targets = FLOW_TARGETS.filter((k) => (totals[k] ?? 0) > 0);
  const ready = rows.reduce((a, r) => a + (r.to !== 'blocked' && r.to !== 'bevaring' ? r.tonnes : 0), 0);
  return { rows, omitted, totals, targets, ready, blocked: totals.blocked ?? 0 };
}

// ------------------------------------------------------------- wording --

/** The colour of a target, as a token name. */
export function flowColorToken(to: FlowTarget): string {
  return to === 'blocked' ? '--tone-crit-ink' : `--circ-${to}`;
}

export function flowLabel(to: FlowTarget): string {
  return to === 'blocked' ? 'Blokeret' : TREATMENT_LABEL[to];
}

/** Why a blocked row waits: its first reason (sample before review). */
export function flowWhy(r: Pick<FlowRow, 'reasons'>): string {
  if (r.reasons.includes('sample')) return 'venter på prøvesvar';
  if (r.reasons.includes('review')) return 'venter på godkendelse';
  return 'mangler tons';
}

export function truncate(s: string, n = 24): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The svg's accessible name: every target's tonnes and the two sums. */
export function flowAria(fd: FlowData): string {
  const parts = fd.targets.map((k) => `${flowLabel(k)} ${formatTonnes(fd.totals[k] ?? 0)}`).join(', ');
  return `Materialeflow: ${parts}. ${formatTonnes(fd.ready)} klar, ${formatTonnes(fd.blocked)} blokeret.`;
}

/** What the hovered or focused node or ribbon carries (`L:<id>`, `R:<target>`, `<id>|<target>`); '' for nothing. */
export function flowInfo(fd: FlowData, key: string | null): string {
  if (!key) return '';
  if (key.startsWith('R:')) {
    const to = key.slice(2) as FlowTarget;
    const rows = fd.rows.filter((r) => r.to === to);
    if (to === 'blocked') {
      const byWhy = new Map<string, number>();
      for (const r of rows) byWhy.set(flowWhy(r), (byWhy.get(flowWhy(r)) ?? 0) + r.tonnes);
      return [...byWhy].map(([why, t]) => `${formatTonnes(t)} ${why}`).join(' · ');
    }
    const stays = to === 'bevaring' ? ' · bliver i bygningen' : '';
    return `${flowLabel(to)} · ${formatTonnes(fd.totals[to] ?? 0)} fra ${plural(rows.length, 'type', 'typer')}${stays}`;
  }
  const id = Number(key.startsWith('L:') ? key.slice(2) : key.split('|')[0]);
  const r = fd.rows.find((x) => x.typeId === id);
  if (!r) return '';
  return `${r.name} → ${flowLabel(r.to)} · ${formatTonnes(r.tonnes)}${r.to === 'blocked' ? ` · ${flowWhy(r)}` : ''}`;
}

export const FLOW_EMPTY = 'Ingen typer har tons endnu. Flowet vises, når typerne i Ressourcer har en vægt.';

/** "2 typer uden tons er ikke med: Tagpap, Døre."; null when every type is drawn. */
export function omittedNote(fd: FlowData): string | null {
  const n = fd.omitted.length;
  if (n === 0) return null;
  return `${plural(n, 'type', 'typer')} uden tons er ikke med: ${fd.omitted.map((t) => t.name).join(', ')}.`;
}

// ------------------------------------------------------------- geometry --

export interface FlowGeometry {
  /** The viewBox width. */
  width: number;
  /** The left nodes' x (their labels sit to its left). */
  x0: number;
  /** The right nodes' x (their labels sit to its right). */
  x1: number;
  nodeWidth: number;
  /** Between two slots. */
  gap: number;
  /** A node's slot is never shorter: its two label lines need it. */
  minSlot: number;
  /** The taller column's height the scale aims at. */
  fit: number;
  /** Top and bottom margin. */
  y0: number;
  /** Between a node and its label. */
  labelGap: number;
  /** Extra room above Blokeret, apart from the treatments. */
  blockedGap: number;
}

export const FLOW_GEOMETRY: FlowGeometry = {
  width: 860,
  x0: 210,
  x1: 640,
  nodeWidth: 9,
  gap: 11,
  minSlot: 32,
  fit: 380,
  y0: 12,
  labelGap: 8,
  blockedGap: 8,
};

/** A node's drawn rectangle is at least this tall, so a sliver still shows. */
const MIN_RECT = 2;

export interface FlowNode {
  /** `L:<typeId>` or `R:<target>`. */
  key: string;
  side: 'left' | 'right';
  x: number;
  y: number;
  /** The tonnes' height (ribbons stack to exactly this). */
  h: number;
  /** The drawn rectangle's height (`h`, at least a sliver). */
  rectH: number;
  label: string;
  sub: string;
  /** Where the two label lines sit. */
  textX: number;
  anchor: 'start' | 'end';
  labelY: number;
  subY: number;
  /** A token name: the target's colour (a type's node takes where it goes, drawn fainter). */
  colorToken: string;
  /** The node's tooltip. */
  title: string;
  /** Its accessible name, with what activating it does. */
  aria: string;
  typeId?: number;
  target?: FlowTarget;
}

export interface FlowRibbon {
  /** `<typeId>|<target>`. */
  key: string;
  typeId: number;
  to: FlowTarget;
  /** The cubic's path. */
  d: string;
  /** The band's thickness (the tonnes' height). */
  width: number;
  /** The drawn stroke: a hair thinner than the band, so neighbours part. */
  strokeWidth: number;
  colorToken: string;
  blocked: boolean;
}

export interface FlowLayout {
  width: number;
  height: number;
  nodes: FlowNode[];
  ribbons: FlowRibbon[];
}

const fix = (n: number) => n.toFixed(1);

/** Whether a ribbon belongs to the hovered key (all do when nothing is). */
export function flowMatch(key: string | null, ribbon: Pick<FlowRibbon, 'key' | 'typeId' | 'to'>): boolean {
  if (!key) return true;
  if (key.startsWith('L:')) return String(ribbon.typeId) === key.slice(2);
  if (key.startsWith('R:')) return ribbon.to === key.slice(2);
  return ribbon.key === key;
}

/** The Sankey's nodes and ribbons for `fd` (see the module comment). */
export function flowLayout(fd: FlowData, g: FlowGeometry = FLOW_GEOMETRY): FlowLayout {
  const left = fd.rows.map((r) => r.tonnes);
  const right = fd.targets.map((k) => fd.totals[k] ?? 0);
  const colH = (vals: number[], sc: number) => vals.reduce((a, v) => a + Math.max(v * sc, g.minSlot), 0) + g.gap * (vals.length - 1);

  // Fit the taller column: the slots at their minimum are fixed, the rest
  // scale. The scale never drops below the heaviest type filling a slot: with
  // many rows the minimum slots alone exceed `fit`, and the drawing grows
  // taller instead of turning every band into a sliver. (The heaviest type,
  // not target: a target sums its types, so flooring on it would still leave
  // every type a sliver.)
  const maxV = Math.max(0, ...left);
  const minSc = maxV > 0 ? g.minSlot / maxV : 0;
  let sc = Math.max(minSc, g.fit / Math.max(1e-6, Math.max(left.reduce((a, v) => a + v, 0), 1)));
  for (let it = 0; it < 6; it++) {
    for (const vals of [left, right]) {
      const big = vals.filter((v) => v * sc >= g.minSlot);
      const fixed = (vals.length - big.length) * g.minSlot + g.gap * (vals.length - 1);
      const sum = big.reduce((a, v) => a + v, 0);
      if (sum > 0 && fixed < g.fit && colH(vals, sc) > g.fit) sc = Math.max(minSc, (g.fit - fixed) / sum);
    }
  }

  // Each node centred in its slot; `o` is how much of it the ribbons have used.
  const pos = new Map<string, { y: number; h: number; o: number }>();
  let y = g.y0;
  for (const r of fd.rows) {
    const h = r.tonnes * sc;
    const slot = Math.max(h, g.minSlot);
    pos.set(`L:${r.typeId}`, { y: y + (slot - h) / 2, h, o: 0 });
    y += slot + g.gap;
  }
  const leftEnd = y;
  y = g.y0;
  fd.targets.forEach((k, i) => {
    // Blokeret stands apart from the treatments, whichever comes before it.
    if (k === 'blocked' && i > 0) y += g.blockedGap;
    const h = (fd.totals[k] ?? 0) * sc;
    const slot = Math.max(h, g.minSlot);
    pos.set(`R:${k}`, { y: y + (slot - h) / 2, h, o: 0 });
    y += slot + g.gap;
  });
  const height = Math.ceil(Math.max(leftEnd, y) + g.y0);

  const xa = g.x0 + g.nodeWidth;
  const xm = (g.x0 + g.x1) / 2;
  const ribbons: FlowRibbon[] = fd.rows.map((r) => {
    const a = pos.get(`L:${r.typeId}`)!;
    const b = pos.get(`R:${r.to}`)!;
    const w = r.tonnes * sc;
    const ya = a.y + a.o + w / 2;
    const yb = b.y + b.o + w / 2;
    a.o += w;
    b.o += w;
    return {
      key: `${r.typeId}|${r.to}`,
      typeId: r.typeId,
      to: r.to,
      d: `M${xa} ${fix(ya)} C${xm} ${fix(ya)} ${xm} ${fix(yb)} ${g.x1} ${fix(yb)}`,
      width: w,
      strokeWidth: Math.max(1.5, w - 1),
      colorToken: flowColorToken(r.to),
      blocked: r.to === 'blocked',
    };
  });

  const node = (
    key: string,
    side: FlowNode['side'],
    label: string,
    sub: string,
    title: string,
    aria: string,
    extra: Pick<FlowNode, 'colorToken' | 'typeId' | 'target'>,
  ): FlowNode => {
    const p = pos.get(key)!;
    const x = side === 'left' ? g.x0 : g.x1;
    const mid = p.y + p.h / 2;
    return {
      key,
      side,
      x,
      y: p.y,
      h: p.h,
      rectH: Math.max(MIN_RECT, p.h),
      label,
      sub,
      textX: side === 'left' ? x - g.labelGap : x + g.nodeWidth + g.labelGap,
      anchor: side === 'left' ? 'end' : 'start',
      labelY: mid - 2,
      subY: mid + 12,
      title,
      aria,
      ...extra,
    };
  };

  const nodes: FlowNode[] = [
    ...fd.rows.map((r) => {
      const tons = formatTonnes(r.tonnes);
      const to = `${flowLabel(r.to)}${r.to === 'blocked' ? ` (${flowWhy(r)})` : ''}`;
      return node(`L:${r.typeId}`, 'left', truncate(r.name), tons, `${r.name} · ${tons} → ${to}`, `${r.name} · ${tons} til ${to}. Åbn typen`, {
        colorToken: flowColorToken(r.to),
        typeId: r.typeId,
      });
    }),
    ...fd.targets.map((k) => {
      const sub = `${formatTonnes(fd.totals[k] ?? 0)}${k === 'bevaring' ? ' · indgår ikke' : ''}`;
      const go = k === 'blocked' ? 'Gå til Det, der blokerer' : 'Gå til pkt. 10';
      return node(`R:${k}`, 'right', flowLabel(k), sub, `${flowLabel(k)} · ${sub}`, `${flowLabel(k)} · ${sub}. ${go}`, {
        colorToken: flowColorToken(k),
        target: k,
      });
    }),
  ];

  return { width: g.width, height, nodes, ribbons };
}
